/**
 * DocumentLinkBlock Component
 * Custom BlockNote block for wiki-style [[Document Name]] links
 * Displays as an inline chip/pill with hover preview
 */

import { useState, useCallback, useRef } from 'react';
import { createReactBlockSpec } from '@blocknote/react';
import { FileText, ExternalLink, Loader2, AlertCircle } from 'lucide-react';

// Props schema for the Document Link block
export interface DocumentLinkProps {
  documentId: string;
  documentName: string;
  preview: string;
  /**
   * Optional BlockNote block id in the target doc to scroll to. When set,
   * clicking the link navigates to /documents/:documentId#block:<targetBlockId>
   * and DocumentationReader's hash effect scrolls/flashes that paragraph.
   */
  targetBlockId: string;
}

// Default props
const defaultProps: DocumentLinkProps = {
  documentId: '',
  documentName: '',
  preview: '',
  targetBlockId: '',
};

/**
 * Tooltip component for document preview
 */
interface PreviewTooltipProps {
  documentName: string;
  preview: string;
  isLoading?: boolean;
  error?: string;
  position: { top: number; left: number };
}

function PreviewTooltip({
  documentName,
  preview,
  isLoading,
  error,
  position,
}: PreviewTooltipProps) {
  return (
    <div
      className="fixed z-50 w-72 bg-white rounded-xl shadow-xl border border-stone-200
                 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      {/* Header */}
      <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-500" />
          <span className="font-medium text-stone-800 truncate">
            {documentName}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-red-500 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        ) : preview ? (
          <p className="text-sm text-stone-600 line-clamp-4">{preview}</p>
        ) : (
          <p className="text-sm text-stone-400 italic">No preview available</p>
        )}
      </div>

      {/* Footer with hint */}
      <div className="px-4 py-2 bg-stone-50 border-t border-stone-100">
        <span className="text-xs text-stone-400">
          Click to open document
        </span>
      </div>
    </div>
  );
}

/**
 * The React component for rendering the Document Link block (inline style)
 */
interface DocumentLinkComponentProps {
  block: {
    id: string;
    props: DocumentLinkProps;
  };
  // Editor is typed loosely to support any BlockNote schema
  editor: unknown;
}

function DocumentLinkComponent({
  block,
}: DocumentLinkComponentProps) {
  const { documentId, documentName, preview, targetBlockId } = block.props;
  const [showTooltip, setShowTooltip] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const linkRef = useRef<HTMLSpanElement>(null);
  const tooltipTimeoutRef = useRef<number | null>(null);

  // Handle mouse enter - show tooltip with delay
  const handleMouseEnter = useCallback(() => {
    tooltipTimeoutRef.current = window.setTimeout(() => {
      if (linkRef.current) {
        const rect = linkRef.current.getBoundingClientRect();
        setTooltipPosition({
          top: rect.bottom + 8,
          left: Math.max(10, rect.left - 100), // Center tooltip, but keep it on screen
        });
      }
      setShowTooltip(true);
    }, 300);
  }, []);

  // Handle mouse leave - hide tooltip
  const handleMouseLeave = useCallback(() => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setShowTooltip(false);
  }, []);

  // Handle click - navigate to document
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!documentId) return;

      setIsNavigating(true);

      // Dispatch custom event for navigation
      // This allows the parent component to handle navigation
      const event = new CustomEvent('documentLinkClick', {
        detail: { documentId, documentName, targetBlockId: targetBlockId || undefined },
        bubbles: true,
      });
      linkRef.current?.dispatchEvent(event);

      // Also try to navigate directly if we're in a router context
      // The parent should handle this event and navigate appropriately
      setTimeout(() => setIsNavigating(false), 500);
    },
    [documentId, documentName]
  );

  // If no document name, show placeholder
  if (!documentName) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-sm
                      bg-stone-100 text-stone-400 rounded-md border border-dashed border-stone-300">
        <FileText className="w-3.5 h-3.5" />
        <span className="italic">No document linked</span>
      </span>
    );
  }

  return (
    <>
      <span
        ref={linkRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`
          inline-flex items-center gap-1.5 px-2.5 py-1
          text-sm font-medium rounded-lg cursor-pointer
          transition-all duration-150 select-none
          ${isNavigating
            ? 'bg-blue-200 text-blue-800'
            : 'bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700'
          }
          border border-blue-200 hover:border-blue-300
        `}
        data-document-id={documentId}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick(e as unknown as React.MouseEvent);
          }
        }}
      >
        {isNavigating ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <FileText className="w-3.5 h-3.5" />
        )}
        <span className="max-w-48 truncate">{documentName}</span>
        <ExternalLink className="w-3 h-3 opacity-50" />
      </span>

      {/* Preview tooltip */}
      {showTooltip && (
        <PreviewTooltip
          documentName={documentName}
          preview={preview}
          position={tooltipPosition}
        />
      )}
    </>
  );
}

/**
 * Wrapper component that renders the inline link in a block container
 */
function DocumentLinkBlockWrapper(props: DocumentLinkComponentProps) {
  return (
    <div className="my-2">
      <DocumentLinkComponent {...props} />
    </div>
  );
}

/**
 * BlockNote schema definition for the Document Link block
 */
export const documentLinkSchema = createReactBlockSpec(
  {
    type: 'documentLink' as const,
    propSchema: {
      documentId: {
        default: defaultProps.documentId,
      },
      documentName: {
        default: defaultProps.documentName,
      },
      preview: {
        default: defaultProps.preview,
      },
      targetBlockId: {
        default: defaultProps.targetBlockId,
      },
    },
    content: 'none',
  },
  {
    render: (props) => {
      // Cast props appropriately for our component
      const blockProps = {
        block: {
          id: props.block.id,
          props: props.block.props as unknown as DocumentLinkProps,
        },
        editor: props.editor,
      };
      return <DocumentLinkBlockWrapper {...blockProps} />;
    },
  }
);

/**
 * Export the block component for direct use
 */
export const DocumentLinkBlock = DocumentLinkComponent;

/**
 * Helper function to insert a document link block
 * @param editor - The BlockNote editor instance
 * @param props - Partial props for the document link
 */
export function insertDocumentLink(
  editor: {
    insertBlocks: (
      blocks: Array<{ type: string; props: Record<string, unknown> }>,
      referenceBlock: unknown,
      placement: 'before' | 'after'
    ) => void;
    getTextCursorPosition: () => { block: unknown };
  },
  props: Partial<DocumentLinkProps>
): void {
  editor.insertBlocks(
    [
      {
        type: 'documentLink',
        props: {
          documentId: props.documentId || '',
          documentName: props.documentName || '',
          preview: props.preview || '',
          targetBlockId: props.targetBlockId || '',
        },
      },
    ],
    editor.getTextCursorPosition().block,
    'after'
  );
}

/**
 * Parse wiki-style [[Document Name]] syntax and extract document name
 * @param text - The text to parse
 * @returns Object with name and remaining text, or null if no match
 */
export function parseDocumentLink(text: string): { name: string; remaining: string } | null {
  const match = text.match(/\[\[([^\]]+)\]\]/);
  if (!match) return null;

  return {
    name: match[1].trim(),
    remaining: text.replace(match[0], '').trim(),
  };
}

/**
 * Inline mark schema for document links (for use within paragraphs)
 * This can be used alongside the block version for inline [[links]]
 */
export const documentLinkMark = {
  name: 'documentLink',
  // Mark properties that will be stored
  inclusive: false,
  parseDOM: [
    {
      tag: 'span[data-document-link]',
      getAttrs: (node: HTMLElement) => ({
        documentId: node.getAttribute('data-document-id'),
        documentName: node.getAttribute('data-document-name'),
        targetBlockId: node.getAttribute('data-target-block-id') || '',
      }),
    },
  ],
  toDOM: (mark: { attrs: { documentId: string; documentName: string; targetBlockId?: string } }) => [
    'span',
    {
      'data-document-link': 'true',
      'data-document-id': mark.attrs.documentId,
      'data-document-name': mark.attrs.documentName,
      ...(mark.attrs.targetBlockId ? { 'data-target-block-id': mark.attrs.targetBlockId } : {}),
      class: 'document-link-inline',
    },
    0,
  ],
  attrs: {
    documentId: { default: '' },
    documentName: { default: '' },
    targetBlockId: { default: '' },
  },
};
